import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReceiptView } from "@/components/ReceiptView";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";
import { formatMoney } from "@/lib/constants";

export const PLAN_FEATURES = {
  basic: ["Скачивание всех файлов", "AI-помощник по всем предметам", "30 дней доступа"],
  pro: ["Всё из Basic", "AI-помощник по всем предметам", "30 дней доступа"],
};

function PlanPick({ settings, onPick, busy }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {["basic", "pro"].map((p) => (
        <div key={p} className={`rounded-2xl border-2 p-5 flex flex-col ${p === "pro" ? "border-[#1f5f42] bg-[#f3f9f5]" : "border-[#d1e7dd]"}`} data-testid={`plan-card-${p}`}>
          <div className="flex items-center gap-2 font-display font-extrabold text-xl text-[#0b2e20]">{p === "pro" && <Sparkles className="h-5 w-5 text-amber-500" />}{p === "pro" ? "Pro" : "Basic"}</div>
          <div className="font-display text-2xl font-extrabold text-[#1f5f42] mt-1" data-testid={`plan-price-${p}`}>{formatMoney(settings[`price_${p}`], settings.currency)}<span className="text-sm text-[#6b8a7b] font-semibold"> / 30 дней</span></div>
          <ul className="mt-3 space-y-1.5 text-sm flex-1">
            {PLAN_FEATURES[p].map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-[#2e7d56] shrink-0 mt-0.5" />{f}</li>)}
          </ul>
          <Button disabled={busy} onClick={() => onPick(p)} className="mt-4 rounded-full bg-[#1f5f42] hover:bg-[#174731]" data-testid={`buy-plan-${p}`}>Купить {p === "pro" ? "Pro" : "Basic"}</Button>
        </div>
      ))}
    </div>
  );
}

function PayStep({ data, onPaid }) {
  const { order, donation_url: url, demo } = data;
  const [busy, setBusy] = useState(false);
  const run = async (path) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/billing/orders/${order.id}/${path}`);
      if (res.paid) onPaid(res); else toast.message(res.message);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="pay-step">
      <ol className="space-y-3 text-sm text-[#0b2e20] list-decimal pl-5">
        <li>Нажмите «Перейти к оплате» и отправьте донат на сумму <b data-testid="pay-amount">{formatMoney(order.amount, order.currency)}</b>.</li>
        <li>В сообщении к донату обязательно укажите код:
          <div className="mt-2 flex items-center gap-2">
            <code className="px-3 py-2 rounded-lg bg-[#e8f5ed] border border-dashed border-[#2e7d56] font-mono font-bold text-lg" data-testid="pay-code">{order.code}</code>
            <button onClick={() => { navigator.clipboard?.writeText(order.code); toast.success("Код скопирован"); }} className="h-9 w-9 grid place-items-center rounded-full hover:bg-[#e8f5ed]" data-testid="pay-copy-code"><Copy className="h-4 w-4" /></button>
          </div>
        </li>
        <li>Вернитесь и нажмите «Я оплатил — проверить». Чек появится автоматически.</li>
      </ol>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 h-11 rounded-full border-2 border-[#1f5f42] text-[#1f5f42] font-bold hover:bg-[#e8f5ed]" data-testid="pay-donation-link"><ExternalLink className="h-4 w-4" />Перейти к оплате</a>
      ) : <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3" data-testid="pay-no-link">Администратор ещё не добавил ссылку на страницу оплаты.</p>}
      {demo ? (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="pay-demo-note">
          <p className="text-sm text-amber-800">Сейчас оплата работает в демо-режиме: деньги не списываются.</p>
          <Button disabled={busy} onClick={() => run("demo-pay")} className="w-full rounded-full bg-amber-500 hover:bg-amber-600" data-testid="pay-demo-button">Оплатить (демо)</Button>
        </div>
      ) : (
        <Button disabled={busy} onClick={() => run("check")} className="w-full rounded-full bg-[#1f5f42] hover:bg-[#174731] h-11" data-testid="pay-check-button">{busy ? "Проверяем..." : "Я оплатил — проверить"}</Button>
      )}
    </div>
  );
}

export function UpgradeDialog() {
  const { upgrade, closeUpgrade, settings, setUser } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => { closeUpgrade(); setOrder(null); setReceipt(null); };

  const pick = async (plan) => {
    setBusy(true);
    try { const { data } = await api.post("/billing/orders", { plan }); setOrder(data); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const paid = (res) => { setUser(res.me); toast.success("Оплата прошла! Тариф активирован"); close(); navigate(`/afterpayment?receipt=${res.receipt.id}`); };

  if (!settings) return null;
  return (
    <Dialog open={!!upgrade} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{receipt ? "Спасибо за покупку!" : order ? `Оплата ${order.order.plan === "pro" ? "Pro" : "Basic"}` : "Тарифы Basic и Pro"}</DialogTitle>
          <DialogDescription>{receipt ? "Чек сохранён в разделе «Тарифы»." : upgrade?.reason || "Выберите тариф. Доступ открывается на 30 дней."}</DialogDescription>
        </DialogHeader>
        {receipt ? <ReceiptView r={receipt} /> : order ? <PayStep data={order} onPaid={paid} /> : <PlanPick settings={settings} onPick={pick} busy={busy} />}
      </DialogContent>
    </Dialog>
  );
}
